import React, {createContext, useContext, useEffect, useState} from "react";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {fillAssociatedEntityLevel, fillEntityLevel} from "../../../state/slices/CoreEntity";
import apiClient from "../../../auth/apiClient";
import {useEnqueueSnackbar} from "../../../hooks/useEnqueueSnackbar";
import * as c from "../../../constants";
import _ from "lodash";
import DeviceControlService from "../../../services/DeviceControlService";
import {updateEntitySummary, updateOrgTreeNoPermissions} from "../../../state/slices/OrgTree";


const deviceControlService = new DeviceControlService(apiClient);

const EntitiesSelectedContext = createContext({});

export const useEntitySelectedContext = () => useContext(EntitiesSelectedContext);


const getAssociatedEntities = (entityID, entityList) => {
    const associatedEntityIds = new Set();

    const directlyAssociated = entityList.filter(entity =>
        entity.parententityid === entityID ||
        entity.entityid === entityID
    );

    directlyAssociated.forEach(entity => {
        associatedEntityIds.add(entity.entityid);
    });

    let foundNew = true;
    while (foundNew) {
        foundNew = false;

        entityList.forEach(entity => {
            if (!associatedEntityIds.has(entity.entityid) &&
                entity.parententityid &&
                associatedEntityIds.has(entity.parententityid)) {

                // Found a new descendant
                associatedEntityIds.add(entity.entityid);
                foundNew = true;
            }
        });
    }
    return entityList.filter(entity => associatedEntityIds.has(entity.entityid));
}

const groupEntitiesByParent = (entities) => {
    if (!entities || entities.length === 0) return {};
    const groupedEntities = {};

    const entityIdToName = {};
    let entityParentId = {};
    const entityParentTypeId = {};
    const entityParentTreeDepthId = {};

    entities.forEach(entity => {
        entityIdToName[entity.entityid] = entity.entityname || entity.name || entity.entityid;
        entityParentId[entity.entityid] = {
            parententityid: entity.parententityid
        };
        entityParentTypeId[entity.entityid] = {
            typeid: entity.typeid
        };
        entityParentTreeDepthId[entity.entityid] = {
            depth: entity.treedepth
        };
    });

    entities.forEach(entity => {
        const parentId = entity.parententityid;

        if (!parentId) return;
        const parentName = entityIdToName[parentId] || parentId;
        if (!groupedEntities[parentName]) {
            groupedEntities[parentName] = {
                entityID: parentId,
                children: [],
                parentID: entityParentId[parentId]?.parententityid,
                typeID: entityParentTypeId[parentId]?.typeid || c.ENTITY_TYPE[entityParentTypeId[parentId]?.entitytype],
                depth: entityParentTreeDepthId[parentId]?.depth
            };
        }

        groupedEntities[parentName].children.push({...entity});
    });

    return groupedEntities;
};

export const EntitySelectedProvider = ({children}) => {
    const [associatedEntities, setAssociatedEntities] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const contextID = useSelector(state => state.coreEntities.ContextID, shallowEqual);
    const [entitySelected, setEntitySelected] = useState(contextID);
    const userID = useSelector(state => state.user.UserID, shallowEqual);
    const isAdmin = useSelector(state => state.user.isAdmin, shallowEqual);
    const OrgEntities = useSelector((state) => state.orgTree.entities, shallowEqual);
    const rootEntity = useSelector((state) => {
        const entities = state.coreEntities?.entities || {};
        return Object.values(entities).find(entity => !entity?.parententityid)?.entityid;
    }, shallowEqual);
    const dispatch = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();

    const [entityDetailsState, setEntityDetailsState] = useState({
        entityID: "",
        isOpen: false
    });

    useEffect(() => {
        async function loadAndSetEntities() {
            setIsLoading(true);

            try {
                const entities = await getNewEntity(rootEntity,userID, isAdmin);
                if (entities) {
                    const groupedEntities = _.cloneDeep(groupEntitiesByParent(entities));

                    setAssociatedEntities(groupedEntities);
                    await dispatch(updateOrgTreeNoPermissions({
                        entities
                    }))
                    await dispatch(updateEntitySummary({
                        entityID:contextID,
                        userID: userID
                    }))
                }

                setEntitySelected(contextID);
                setIsLoading(false);
            } catch (error) {
                enqueueSnackbar("Error loading entities", {
                    variant: "error",
                    tag: "FailedToLoadEntities",
                });
                setIsLoading(false);
            }
        }

        loadAndSetEntities();
    }, [contextID, rootEntity]);

    // update the associated entities when the OrgEntities change (new entity added or deleted)
    useEffect(() => {
        if (entitySelected) {
            const updatedAssociatedEntities = getAssociatedEntities(
                entitySelected,
                Object.values(OrgEntities)
            );

            const groupedEntities = groupEntitiesByParent(updatedAssociatedEntities);
            setAssociatedEntities(groupedEntities);
        }
    }, [OrgEntities, entitySelected]);

    const handleEntityDetailSelection = (entityID, openSetting = true) => {
        setEntitySelected(entityID)
        setEntityDetailsState({entityID: entityID, isOpen: openSetting});
    };


    const getNewEntity = async (entityID, userID, isAdmin) => {
        try {
            const response = await deviceControlService.GetSidePanelData(entityID, userID, isAdmin);
            if (!response || response.data.length === 0) return null;

            return response.data;

        } catch (error) {
            console.log("error", error);
            enqueueSnackbar("failed to get side panel data", {
                variant: "error",
                tag: "FailedToGetEntities",
            });
            return null;
        }
    }

    const onSelectedEntity = async (entity) => {
        if (entity.entityid === entitySelected) return;

        if (entity.entityid === contextID && contextID !== entitySelected) {
            setEntitySelected(contextID); //context entity is already selected
            const contextAssociatedEntities = getAssociatedEntities(entity.entityid, Object.values(OrgEntities))
            const groupedEntities = groupEntitiesByParent(contextAssociatedEntities);
            setAssociatedEntities(groupedEntities);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const foundEntity = OrgEntities[entity.entityid];

            if (foundEntity && foundEntity.details) {
                const orgAssociatedEntities = getAssociatedEntities(entity.entityid, Object.values(OrgEntities))

                const groupedEntities = groupEntitiesByParent(orgAssociatedEntities);
                setEntitySelected(entity.entityid);
                setAssociatedEntities(groupedEntities);
                setIsLoading(false);
                return;
            }

            await dispatch(updateEntitySummary({
                entityID: entity.entityid,
                userID: userID
            }))


            if (!entity.haschildren) {
                setIsLoading(false);
                setEntitySelected(entity.entityid);
                return;
            }

            setEntitySelected(entity.entityid);

            setIsLoading(false);

        } catch (error) {
            enqueueSnackbar("Failed to select entity", {
                variant: "error",
                tag: "FailedToSelectEntity",
            });
            setIsLoading(false);

        }
    }

    return (
        <EntitiesSelectedContext.Provider value={{
            associatedEntities,
            entitySelected,
            onSelectedEntity,
            isLoading,
            entityDetailsState,
            handleEntityDetailSelection
        }}>
            {children}
        </EntitiesSelectedContext.Provider>
    );
};
